import { useState, useEffect } from "react";
import { UiModal } from "../../../../Components/Shared/UiModal";

const EMPTY_FORM = {
  zoneId: "",
  minDistance: "",
  maxDistance: "",
  multiplier: "",
};

export default function InStationZoneModal({
  isOpen,
  onClose,
  onSubmit,
  editData,
}) {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!isOpen) {
      setFormData(EMPTY_FORM);
      setErrors({});
      return;
    }

    if (editData) {
      setFormData({
        zoneId: editData.zone_name || "",
        minDistance: editData.min_distance || "",
        maxDistance: editData.max_distance || "",
        multiplier: editData.multiplier || "",
      });
    } else {
      setFormData(EMPTY_FORM);
    }
    setErrors({});
  }, [isOpen, editData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "zoneId") {
      setFormData((prev) => ({ ...prev, [name]: value }));
    } else {
      const regex = /^\d*\.?\d*$/;
      if (regex.test(value) || value === "") {
        setFormData((prev) => ({ ...prev, [name]: value }));
      }
    }

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.zoneId) {
      newErrors.zoneId = "Zone name is required";
    }

    if (!formData.minDistance) {
      newErrors.minDistance = "Minimum Distance is required";
    }

    if (!formData.maxDistance) {
      newErrors.maxDistance = "Maximum Distance is required";
    }

    if (formData.minDistance && formData.maxDistance) {
      const min = parseFloat(formData.minDistance);
      const max = parseFloat(formData.maxDistance);
      if (min >= max) {
        newErrors.maxDistance =
          "Maximum Distance must be greater than Minimum Distance";
      }
    }

    if (!formData.multiplier) {
      newErrors.multiplier = "Zone Multiplier is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      const submitData = {
        zone_name: formData.zoneId,
        min_distance: parseFloat(formData.minDistance),
        max_distance: parseFloat(formData.maxDistance),
        multiplier: parseFloat(formData.multiplier),
      };
      onSubmit(submitData);
    }
  };

  const isEditMode = Boolean(editData);
  const fieldClass = (name) =>
    `ui-input-field ${errors[name] ? "border-brand-error" : ""}`;

  return (
    <UiModal
      isOpen={isOpen}
      onClose={onClose}
      eyebrow="Delivery Management"
      title={isEditMode ? "Edit In-Station Zone" : "Add In-Station Zone"}
      as="form"
      onSubmit={handleSubmit}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="ui-btn-cancel min-w-[108px]"
          >
            Cancel
          </button>
          <button type="submit" className="ui-btn-save min-w-[108px]">
            {isEditMode ? "Update" : "Save"}
          </button>
        </>
      }
    >
      <section className="ui-modal-section">
        <h4 className="ui-modal-section-title">Zone details</h4>
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="ui-form-label">
                In Station Zone Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="zoneId"
                value={formData.zoneId}
                onChange={handleInputChange}
                className={fieldClass("zoneId")}
                placeholder="Enter in station zone name"
              />
              {errors.zoneId ? (
                <p className="ui-form-error">{errors.zoneId}</p>
              ) : null}
            </div>
            <div>
              <label className="ui-form-label">
                In Station Zone Multiplier{" "}
                <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="multiplier"
                value={formData.multiplier}
                onChange={handleInputChange}
                className={fieldClass("multiplier")}
                placeholder="e.g., 1, 2, 3.4"
              />
              {errors.multiplier ? (
                <p className="ui-form-error">{errors.multiplier}</p>
              ) : null}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="ui-form-label">
                In Station Minimum Distance{" "}
                <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="minDistance"
                value={formData.minDistance}
                onChange={handleInputChange}
                className={fieldClass("minDistance")}
                placeholder="e.g., 1, 2, 3.4"
              />
              {errors.minDistance ? (
                <p className="ui-form-error">{errors.minDistance}</p>
              ) : null}
            </div>
            <div>
              <label className="ui-form-label">
                In Station Maximum Distance{" "}
                <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="maxDistance"
                value={formData.maxDistance}
                onChange={handleInputChange}
                className={fieldClass("maxDistance")}
                placeholder="e.g., 1, 2, 3.4"
              />
              {errors.maxDistance ? (
                <p className="ui-form-error">{errors.maxDistance}</p>
              ) : null}
            </div>
          </div>
        </div>
      </section>
    </UiModal>
  );
}
